import type { DomainType, Metadata } from '@/types';
import domainContentJson from './domainContent.json';

interface DomainTextSection {
  title: string;
  subtitle: string;
  description: string;
  description2: string;
}

interface DomainCardSection extends DomainTextSection {
  link: string;
  buttonText: string;
  image: string;
}

export interface DomainContentEntry {
  label: string;
  aioEnabled: boolean;
  frontpage: DomainCardSection;
  home: DomainTextSection;
}

type DomainStats = Metadata['domains'][string];

const domainContent = domainContentJson as Record<DomainType, DomainContentEntry>;
export const DOMAIN_ORDER = Object.keys(domainContent) as DomainType[];

/** Footprints hidden from the public site (routes, home carousel, cross-links). Admin still uses full DOMAIN_ORDER. */
export const HIDDEN_PUBLIC_DOMAINS: DomainType[] = ['mediadiplomatic'];
export const PUBLIC_DOMAIN_ORDER = DOMAIN_ORDER.filter(
  (domain) => !HIDDEN_PUBLIC_DOMAINS.includes(domain)
);

function withStats(template: string, stats?: DomainStats): string {
  return template
    .replace(/\{orgs\}/g, String(stats?.orgs ?? 0))
    .replace(/\{terms\}/g, String(stats?.terms ?? 0))
    .replace(/\{cities\}/g, String(stats?.cities ?? 0));
}

export function getDomainContent(domain: DomainType): DomainContentEntry {
  return domainContent[domain];
}

export function getDomainLabel(domain: DomainType): string {
  return getDomainContent(domain).label;
}

export function isAIOEnabled(domain: DomainType): boolean {
  return getDomainContent(domain).aioEnabled;
}

/** Domains that default to Google AI Overview in dual-mode pages (Geneva-style). */
export function defaultsToAIO(domain: DomainType): boolean {
  return domain === 'geneva' || domain === 'un-ai';
}

export function getNoActorsLabel(domain: DomainType): string | undefined {
  if (domain === 'geneva') return 'No Geneva Actors';
  if (domain === 'un-ai') return 'No UN Actors';
  if (domain === 'un') return 'No UN Actors';
  return undefined;
}

export function getDomainHomeContent(domain: DomainType, stats?: DomainStats): DomainTextSection {
  const content = getDomainContent(domain).home;
  return {
    title: content.title,
    subtitle: content.subtitle,
    description: withStats(content.description, stats),
    description2: withStats(content.description2, stats),
  };
}

export function getFrontpageCards(domainStats?: Metadata['domains']): DomainCardSection[] {
  return PUBLIC_DOMAIN_ORDER.map((domain) => {
    const content = getDomainContent(domain).frontpage;
    const stats = domainStats?.[domain];

    return {
      ...content,
      description: withStats(content.description, stats),
      description2: withStats(content.description2, stats),
    };
  });
}
